import { DownOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useAEPNSocket } from '@nxsys/hooks/useAEPNSocket';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { showMessage } from '@nxsys/utils/message';
import { Icons } from '@nxsys/types/icons';
import { Dropdown } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ContributionsSubmittedTab from './ContributionsSubmittedTab';
import ContributionsPendingTab from './ContributionsPendingTab';

export enum CONTRIBUTIONS_IRELAND_TAB {
  SUBMITTED = 'SUBMITTED',
  PENDING = 'PENDING',
  ERRORS = 'ERRORS',
}

export type ContributionsIrelandTabRef = {
  exportSelected?: () => void;
  exportAll?: () => void;
  exportErrorsSelected?: () => void;
  exportAllErrors?: () => void;
  resubmit?: () => void;
  markAsSubmitted?: () => void;
  checkStatus?: () => void;
  submit?: () => void;
};

const ContributionsReportIreland: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { goBack } = useGoBackByQuery();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as CONTRIBUTIONS_IRELAND_TAB;

  const { user } = useSelector((state: AppState) => state.appStore);
  const tabRef = useRef<ContributionsIrelandTabRef>(null);

  const [activeTab, setActiveTab] = useState<CONTRIBUTIONS_IRELAND_TAB>(
    CONTRIBUTIONS_IRELAND_TAB.SUBMITTED,
  );

  useEffect(() => {
    if (tabParam) setActiveTab(tabParam);
  }, [tabParam]);

  const tabs = useMemo(
    () => [
      {
        tab: 'Pension Contributions Reports',
        key: CONTRIBUTIONS_IRELAND_TAB.SUBMITTED,
      },
      {
        tab: 'Pending Submissions',
        key: CONTRIBUTIONS_IRELAND_TAB.PENDING,
      },
      {
        tab: messages['common.errors'],
        key: CONTRIBUTIONS_IRELAND_TAB.ERRORS,
      },
    ],
    [messages],
  );

  const handleTabChange = useCallback(
    (key: CONTRIBUTIONS_IRELAND_TAB) => {
      setActiveTab(key);
      navigate('/reports/pension/contributions-ireland', {
        query: { tab: key },
      });
    },
    [navigate],
  );

  const handleGoBack = () =>
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: '/reports',
      excludeParams: ['tab'],
    });

  // Socket: poll status
  useAEPNSocket({
    eventName: `naersa-poll-status-${user?.id}`,
    onSuccess: (data) => {
      showMessage({
        type: 'success',
        content: data?.message || 'Checking status and errors completed successfully.',
      });
      tabRef.current?.exportSelected?.();
    },
    onError: (err) => {
      showMessage({
        type: 'error',
        content: err || 'Checking status and errors failed.',
      });
    },
  });

  // Socket: submit
  useAEPNSocket({
    eventName: `naersa-submit-contributions-${user?.id}`,
    onSuccess: (data) => {
      showMessage({
        type: 'success',
        content: data?.message || 'Pension contributions submitted successfully.',
      });
    },
    onError: (err) => {
      showMessage({
        type: 'error',
        content: err || 'Submit contributions failed.',
      });
    },
  });

  // Socket: resubmit
  useAEPNSocket({
    eventName: `naersa-resubmit-contributions-${user?.id}`,
    onSuccess: (data) => {
      showMessage({
        type: 'success',
        content: data?.message || 'Resubmit contributions completed successfully.',
      });
    },
    onError: (err) => {
      showMessage({
        type: 'error',
        content: err || 'Resubmit contributions failed.',
      });
    },
  });

  const renderTitle = useCallback(
    () => (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix="Contributions Report"
          separator=""
          title=""
        />
      </div>
    ),
    [handleGoBack],
  );

  const errorsMenuProps = {
    items: [
      {
        key: 'export',
        label: messages['common.export'],
        onClick: () => tabRef.current?.exportErrorsSelected?.(),
      },
      {
        key: 'exportAll',
        label: messages['common.exportAll'],
        onClick: () => tabRef.current?.exportAllErrors?.(),
      },
      {
        key: 'resubmit',
        label: messages['common.resubmit'],
        onClick: () => tabRef.current?.resubmit?.(),
      },
    ],
  };

  const renderRightContent = () => (
    <div className="flex items-center gap-x-4">
      {activeTab === CONTRIBUTIONS_IRELAND_TAB.SUBMITTED && (
        <>
          <AppButton
            name="Check Status & Errors"
            variant="outlined"
            onClick={() => tabRef.current?.checkStatus?.()}
          />
          <AppButton
            name={messages['common.export']}
            variant="outlined"
            type="primary"
            leftIcon={<Icons.IconArrowExport />}
            onClick={() => tabRef.current?.exportSelected?.()}
          />
          <AppButton
            type="primary"
            name={messages['common.exportAll']}
            leftIcon={<Icons.IconArrowExport color="#fff" />}
            onClick={() => tabRef.current?.exportAll?.()}
          />
        </>
      )}
      {activeTab === CONTRIBUTIONS_IRELAND_TAB.PENDING && (
        <>
          <AppButton
            name={messages['common.export']}
            variant="outlined"
            type="primary"
            leftIcon={<Icons.IconArrowExport />}
            onClick={() => tabRef.current?.exportSelected?.()}
          />
          <AppButton
            type="primary"
            name={messages['common.exportAll']}
            leftIcon={<Icons.IconArrowExport color="#fff" />}
            onClick={() => tabRef.current?.exportAll?.()}
          />
          <AppButton
            name={messages['common.submit']}
            onClick={() => tabRef.current?.submit?.()}
          />
        </>
      )}
      {activeTab === CONTRIBUTIONS_IRELAND_TAB.ERRORS && (
        <Dropdown menu={errorsMenuProps}>
          <AppButton
            type="primary"
            name={messages['common.action']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      )}
    </div>
  );

  const renderTabContent = useCallback(() => {
    if (activeTab === CONTRIBUTIONS_IRELAND_TAB.PENDING) {
      return (
        <ContributionsPendingTab
          key={CONTRIBUTIONS_IRELAND_TAB.PENDING}
          tabRef={tabRef}
        />
      );
    }
    return (
      <ContributionsSubmittedTab
        key={activeTab}
        tabRef={tabRef}
        tabType={activeTab}
      />
    );
  }, [activeTab]);

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppCardTabListContainer
          tabList={tabs}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
        >
          {renderTabContent()}
        </AppCardTabListContainer>
      </AppCard>
    </AppsContainer>
  );
};

export default ContributionsReportIreland;
